import React, { lazy } from 'react';
import { ProtectedRoute } from '../auth/auth-config';
import { ProtectedRouteProvider } from '../providers/ProtectedRouteProvider';

// Lazy load components
const Home = lazy(() => import('../components/pages/Home'));
const Services = lazy(() => import('../components/pages/Services'));
const Contacts = lazy(() => import('../components/pages/Contacts'));
const Products = lazy(() => import('../components/pages/Products'));
const About = lazy(() => import('../components/pages/About'));
const Privacy = lazy(() => import('../components/pages/Privacy'));
const Terms = lazy(() => import('../components/pages/Terms'));
const Login = lazy(() => import('../components/pages/Login'));
const NotFound = lazy(() => import('../components/pages/NotFound'));

// Admin components
const AdminLayout = lazy(() => import('../components/pages/admin/AdminLayout'));
const AdminHome = lazy(() => import('../components/pages/admin/AdminHome'));
const AdminDashboard = lazy(() => import('../components/pages/admin/Dashboard'));
const AdminUsers = lazy(() => import('../components/pages/admin/Users'));
const AdminUserDetail = lazy(() => import('../components/pages/admin/UserDetail'));
const MyMessages = lazy(() => import('../components/pages/admin/MyMessages'));
const ConsultantList = lazy(() => import('../components/pages/admin/ConsultantList'));
const SupportTickets = lazy(() => import('../components/pages/admin/SupportTickets'));
const BreedList = lazy(() => import('../components/pages/admin/BreedList'));
const RuntimeConfigs = lazy(() => import('../components/pages/admin/RuntimeConfigs'));
const AiGenerateTraces = lazy(() => import('../components/pages/admin/AiGenerateTraces'));
const VetRawData = lazy(() => import('../components/pages/admin/VetRawData'));
const GraphRAGImporter = lazy(() => import('../components/pages/admin/GraphRAGImporter'));
const Translation = lazy(() => import('../components/pages/admin/Translation'));
const TranslationBatchImport = lazy(() => import('../components/pages/admin/TranslationBatchImport'));

// Route configuration
export const routes = [
  {
    path: '/',
    component: Home,
    exact: true,
    name: 'Home',
    showInNav: true
  },
  {
    path: '/services',
    component: Services,
    name: 'Services',
    showInNav: true
  },
  {
    path: '/products',
    component: Products,
    name: 'Products',
    showInNav: false,
    protected: true // This route requires authentication
  },
  {
    path: '/about-us',
    component: About,
    name: 'About Us',
    showInNav: true
  },
  {
    path: '/contacts',
    component: Contacts,
    name: 'Contacts',
    showInNav: true
  },
  {
    path: '/privacy',
    component: Privacy,
    name: 'Privacy',
    showInNav: false
  },
  {
    path: '/terms',
    component: Terms,
    name: 'Terms',
    showInNav: false
  },
  {
    path: '/admin/login',
    component: Login,
    name: 'Login',
    showInNav: false
  },
  {
    path: '/admin',
    component: AdminHome,
    protected: true,
    adminShell: true,
    children: [
      {
        path: 'dashboard',
        component: AdminDashboard,
        name: 'Dashboard',
        adminShell: true
      },
      {
        path: 'users',
        component: AdminUsers,
        name: 'Users',
        adminShell: true
      },
      {
        path: 'users/:userId',
        component: AdminUserDetail,
        name: 'User Detail',
        adminShell: true
      },
      {
        path: 'messages',
        component: MyMessages,
        name: 'My Messages',
        adminShell: true
      },
      {
        path: 'consultant-list',
        component: ConsultantList,
        name: 'Consultant List',
        adminShell: true
      },
      {
        path: 'support-tickets',
        component: SupportTickets,
        name: 'Support Tickets',
        adminShell: true
      },
      {
        path: 'breed-list',
        component: BreedList,
        name: 'Breed List',
        adminShell: true
      },
      {
        path: 'runtime-configs',
        component: RuntimeConfigs,
        name: 'Runtime Configs',
        adminShell: true
      },
      {
        path: 'ai-generate-traces',
        component: AiGenerateTraces,
        name: 'Ai Generate Traces',
        adminShell: true
      },
      {
        path: 'vet-raw-data',
        component: VetRawData,
        name: 'Vet Raw Data',
        adminShell: true
      },
      {
        path: 'graphrag-importer',
        component: GraphRAGImporter,
        name: 'GraphRAG Importer',
        adminShell: true
      },
      {
        path: 'translation',
        component: Translation,
        name: 'Translation',
        adminShell: true
      },
      {
        path: 'translation/batch-import',
        component: TranslationBatchImport,
        name: 'Translation Batch Import',
        adminShell: true
      },
      {
        path: 'translation/namespaces/:namespaceId',
        component: Translation,
        name: 'Translation Namespace',
        adminShell: true
      },
      {
        path: 'translation/keys/:keyId',
        component: Translation,
        name: 'Translation Key',
        adminShell: true
      }
    ]
  },
  {
    path: '*',
    component: NotFound,
    name: 'Not Found',
    showInNav: false
  }
];

// Helper function to get navigation items
export const getNavItems = () => routes.filter(route => route.showInNav);

// Helper function to get all routes, including child routes
export const getAllRoutes = () => {
  const flatRoutes = [];
  routes.forEach(route => {
    if (route.children && Array.isArray(route.children)) {
      route.children.forEach(child => {
        flatRoutes.push({
          ...child,
          path: route.path.endsWith('/') ? route.path + child.path : route.path + (child.path ? '/' + child.path : ''),
          protected: route.protected || child.protected,
          parent: route,
        });
      });
      // Optionally, include the parent route itself (e.g., /admin)
      flatRoutes.push({ ...route, children: undefined });
    } else {
      flatRoutes.push(route);
    }
  });
  return flatRoutes;
};

// Helper function to wrap protected routes
export const wrapRoute = (route) => {
  const BaseComponent = route.component;

  const ComponentWithShell = (props) => {
    const content = <BaseComponent {...props} />;
    return route.adminShell ? <AdminLayout>{content}</AdminLayout> : content;
  };

  if (route.protected) {
    return {
      ...route,
      component: (props) => (
        <ProtectedRouteProvider>
          <ProtectedRoute>
            <ComponentWithShell {...props} />
          </ProtectedRoute>
        </ProtectedRouteProvider>
      )
    };
  }

  return {
    ...route,
    component: ComponentWithShell
  };
};
